import {
    makeStyles 
} from '@mui/styles';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import ProgressBar from '../components/progressBar';
import {
    MouseEvent, useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import {
    Data, get, post 
} from '../../../networking';
import {
    validateEmail 
} from '../../../lib/utils';
import {
    useCookies 
} from 'react-cookie';
import useAppUserAgent from '../../core/use-app-user-agent';
import {
    colors 
} from '../../../styles/_colors';
import Link from 'next/link';
import {
    ADJUST_EVENTS, AdjustEventPayload, trackAdjustEvent 
} from '../../../lib/adjust';
import {
    DiagnosticFormContext 
} from '../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../figmaTheme';
import SunnysideButton, {
    ButtonSizes, ButtonVariants 
} from '../../../components_sunnyside/button';

async function verifyEmail(email: string): Promise<Data> {
    return await get(`auth/verify?email=${encodeURIComponent(email)}`);
}

async function updateEmail(email: string): Promise<Data> {
    return await post('users/set-email', {
        email 
    });
}

type Props = {
    position: number,
    sectionKey: string,
    backJump?: number
};

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;
const skippyEnvironment = new Set(['development', 'staging']);

const useStyles = makeStyles((theme) => ({
    scrollableContainer: {
        overflow: 'scroll',
        scrollbarWidth: 'none',
        minHeight: '100%',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    textContainer: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '12px'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '12px',
    },
    subtitle: {
        marginBottom: '30px'
    },
    inputContainer: {
        opacity: 0,
        animation: '$fadeIn 0.5s forwards 0.5s',
    },
    label: {
        lineHeight: '24px',
        fontWeight: TypographyWeights.SEMI_BOLD,
        margin: '0 0 4px'
    },
    input: {
        fontSize: '16px',
        padding: '12px 16px',
        borderRadius: '12px',
        outline: 'none',
        width: '100%',
        ['&:invalid']: {
            border: '1px solid red'
        }
    },
    error: {
        textAlign: 'center',
        color: theme.palette.error.main
    },
    loginLink: {
        fontFamily: 'Poppins',
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: TypographyWeights.REGULAR,
        color: colors.sky,
        margin: '0'
    },
    skippy: {
        backgroundColor: theme.palette.error.main,
        margin: '0 auto'
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: '0' 
        },
        '100%': {
            opacity: '1' 
        },
    }
}));

export const EmailInputScreen = ({
    position,
    sectionKey,
    backJump
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const classes = useStyles();
    const theme = useTheme();
    const [cookies] = useCookies();
    const appUserAgent = useAppUserAgent();
    const [value, setValue] = useState(formContext.answers?.email || '');
    const [error, setError] = useState('');
    const [showLoginButton, setShowLoginButton] = useState(false);
    const [showButton, setShowButton] = useState(value.length > 0);
    const inputBox = useRef(null);
    const section = useMemo(() => {
        return formContext?.sections.find(s => s.key === sectionKey);
    }, [sectionKey, formContext?.sections]);

    useEffect(() => {
        if (!inputBox.current) return;

        inputBox.current.focus();
    }, [inputBox.current]);

    const onClick = (e: MouseEvent) => {
        if (e) {
            e.preventDefault();
        }

        handleEmailValidation();
    };

    const handleEmailValidation = async (shouldSkip = false) => {
        if (!value) {
            setError('Your email is required');
            return;
        }

        const isValidEmail = await verifyEmail(value);

        if (!validateEmail(value)) {
            setError('Please input a valid email');
            setShowLoginButton(false);
        } else if (!isValidEmail.isValid) {
            setError('Email already exists');
            setShowLoginButton(true);
        } else if (validateEmail(value)) {
            const formattedEmail = value.trim();
            await updateEmail(formattedEmail);

            if (shouldSkip) {
                formContext.skippySkipsEmail({
                    email: formattedEmail 
                });
                return;
            }

            formContext.setAnswers({
                email: formattedEmail 
            });

            if (inputBox.current) inputBox.current.blur();

            if (cookies?.ss_app === 'true' && (cookies?.ss_adid || cookies?.ss_idfv || cookies?.ss_gps_adid)) {
                const event_token = ADJUST_EVENTS[cookies.ss_app_device]?.email;
                if (event_token) {
                    const payload: AdjustEventPayload = {
                        event_token,
                    };

                    if (cookies.ss_adid) payload.adid = cookies.ss_adid;
                    if (cookies.ss_idfv) payload.idfv = cookies.ss_idfv;
                    if (cookies.ss_gps_adid) payload.gps_adid = cookies.ss_gps_adid;

                    trackAdjustEvent(payload, cookies.ss_app_device);
                }
            }

            setTimeout(() => {
                formContext.goNext();
            }, 50);

            setError('');
            setShowLoginButton(false);
        } else {
            setError('');
            setShowLoginButton(false);
        }
    };

    const onChange = (e) => {
        const value = e.target.value;
        setValue(value.trim());
        setError('');
        setShowLoginButton(false);
    };

    const duplicateEmailUrl = () => {
        if (cookies?.ss_app === 'true' || appUserAgent?.isApp) {
            return `/app-bridge?redirect=${encodeURIComponent(
                `/logout?url=${encodeURIComponent('login/email')}`
            )}&web_app=true`;
        }
        return `/logout?url=${encodeURIComponent('login/email')}`;
    };

    const onKeyPress = (e) => {
        if (!value || error) return;

        if (e.key === 'Enter') handleEmailValidation();
    };

    useEffect(() => {
        if (value.length > 0) setShowButton(true);
        else setShowButton(false);
    }, [value]);

    const onSkippySkips = () => {
        handleEmailValidation(true);
    };

    useEffect(() => {
        if (formContext.answers?.email) {
            setValue(formContext.answers?.email);
            setShowButton(true);
        }
    }, [formContext.answers?.email]);

    return (
        <CardContainer
            sectionKey={sectionKey}
            backJump={backJump}
            goBack={formContext.goBack}
            goNext={showButton && onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={sectionKey}
                />
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Slide
                            in={true}
                            direction={formContext.screenDirection}
                            timeout={500}
                            mountOnEnter
                            unmountOnExit
                        >
                            <Box className={classes.textContainer}>
                                <Typography
                                    variant={TypographyVariants.H3}
                                    dangerouslySetInnerHTML={{
                                        __html: `Let's pause to <span style="color:${section?.colors.main};font-weight:${TypographyWeights.BOLD};">save your progress</span>. Enter your email address to start your profile.` 
                                    }}
                                />
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.subtitle}
                                    dangerouslySetInnerHTML={{
                                        __html: 'We take your privacy seriously and we will never share or sell your data. We use email to share weekly reports that help you track your progress towards your goals.' 
                                    }}
                                />
                            </Box>
                        </Slide>
                        <Box className={classes.inputContainer}>
                            <Typography
                                variant={TypographyVariants.H6}
                                className={classes.label}
                            >
                                Your Email Address
                            </Typography>
                            <input
                                ref={inputBox}
                                autoCorrect={'off'}
                                spellCheck={false}
                                autoFocus
                                autoComplete={'email'}
                                autoCapitalize={'off'}
                                name={'email'}
                                required
                                type={'text'}
                                aria-invalid={'false'}
                                onKeyPress={onKeyPress}
                                className={classes.input}
                                style={{
                                    border: error ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.neutral.light}` 
                                }}
                                placeholder={'Enter your email'}
                                onChange={onChange}
                                value={value}
                            />
                        </Box>
                        <Typography
                            variant={TypographyVariants.H6}
                            className={classes.error}
                        >
                            {error}
                        </Typography>
                        {showLoginButton && (
                            <Link
                                href={duplicateEmailUrl()}
                                passHref
                                prefetch={false}
                            >
                                <a
                                    className={classes.loginLink}
                                >
                                    Please log in instead
                                </a>
                            </Link>
                        )}
                        {showButton && (skippyEnvironment.has(ENVIRONMENT)) && (
                            <SunnysideButton
                                buttonVariant={ButtonVariants.PRIMARY}
                                buttonSize={ButtonSizes.LARGE}
                                onClick={onSkippySkips}
                                className={classes.skippy}
                            >
                                <Typography
                                    style={{
                                        fontWeight: TypographyWeights.BOLD,
                                        color: theme.palette.white
                                    }}
                                >
                                    SKIPPY
                                </Typography>
                            </SunnysideButton>
                        )}
                    </Box>
                    <Box
                        style={{
                            height: '300px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default EmailInputScreen;
